import { Button } from '@mui/joy';
import { PencilSimple } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { FormProvider, useNotificationContext } from 'core/states';
import { IRequestPayload } from 'core/types';
import { pickByClass } from 'core/utils';
import { CorporateSourceType } from 'corporate/enums';
import { useUpsertCorporatePart } from 'corporate/hooks';
import { ICorporatePart, UpdateCorporatePartForm } from 'corporate/types';
import { uomService } from 'parts/services';
import { UpsertCorporatePartUomForm } from 'parts/types';

import { corporatePartUomSchema } from './schema';

interface IProps {
  corporatePart: Partial<ICorporatePart>;
  onClose?: () => void;
}

export const EditCorporatePartUom = ({ corporatePart, onClose }: IProps) => {
  const [isEditing, setIsEditing] = useState(false);

  const { t } = useTranslation();

  const { clearNotification } = useNotificationContext();
  const { upsertCorporatePart } = useUpsertCorporatePart();

  const defaultValues = useMemo(
    () => ({
      ...pickByClass(corporatePart, UpsertCorporatePartUomForm),
      uom: { id: corporatePart.uomId, name: corporatePart.uom },
    }),
    [corporatePart]
  );

  const getUomBaseOptions = (requestPayload: IRequestPayload, signal: AbortSignal) =>
    uomService.getManyUoms({ ...requestPayload, filters: { isBase: 1 } }, signal);

  const onSubmit =
    (reset: UseFormReset<UpsertCorporatePartUomForm>) =>
    async (data: UpsertCorporatePartUomForm) => {
      const { success } = await upsertCorporatePart({
        corporatePart: {
          id: corporatePart.id,
          uomId: data.uom?.id,
          ...data,
        } as unknown as UpdateCorporatePartForm,
      });

      if (success) {
        handleOnClose(reset);
      }
    };

  const handleOnClose = (reset: UseFormReset<UpsertCorporatePartUomForm>) => {
    clearNotification();
    setIsEditing(false);
    onClose?.();
    reset(defaultValues);
  };

  return (
    <>
      <Button startDecorator={<PencilSimple />} onClick={() => setIsEditing(true)}>
        {t('buttonLabelEdit')}
      </Button>
      <FormProvider
        schema={corporatePartUomSchema()}
        defaultValues={defaultValues}
        resetDefaultValues
      >
        <Form
          title={t('titleUpdateCompanyPart')}
          isOpen={isEditing}
          onClose={handleOnClose}
          onSubmit={onSubmit}
        >
          <FormField
            name="uom"
            type={InputType.AUTOCOMPLETE}
            inputProps={{
              label: t('labelBaseUom'),
              required: true,
              disableClearable: true,
              getOptions: getUomBaseOptions,
              optionKey: 'name',
              enabledOnFocus: false,
            }}
          />
          <FormField
            name="purchaseCostUom"
            type={InputType.TEXT_INPUT}
            inputProps={{ label: t('labelPurchaseUom'), disabled: true }}
          />
          <FormField
            name="salesUom"
            type={InputType.TEXT_INPUT}
            inputProps={{ label: t('labelSalesUom') }}
          />
          <FormField
            name="alternateSalesUom"
            type={InputType.TEXT_INPUT}
            inputProps={{ label: t('labelAlternateSalesUom') }}
          />
          <FormField
            name="packageUom"
            type={InputType.TEXT_INPUT}
            inputProps={{ label: t('labelPackageUom') }}
          />
          <FormField
            name="externalUom"
            type={InputType.TEXT_INPUT}
            inputProps={{
              label: t('labelExternalErpUom'),
              disabled: corporatePart.externalUomSource === CorporateSourceType.EXTERNAL,
              tooltip:
                corporatePart.externalUomSource === CorporateSourceType.EXTERNAL &&
                t('labelExternalSource'),
            }}
          />
          <FormField
            name="alternateUom"
            type={InputType.TEXT_INPUT}
            inputProps={{
              label: t('labelSkuUom'),
              disabled: corporatePart.alternateUomSource === CorporateSourceType.EXTERNAL,
              tooltip:
                corporatePart.alternateUomSource === CorporateSourceType.EXTERNAL &&
                t('labelExternalSource'),
            }}
          />
        </Form>
      </FormProvider>
    </>
  );
};
